import { Chip, Grid, TableContainer, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "./AdditionalContentTheme.css";
import { useEffect, useState } from "react";
import { DesignThemeEndPointLogic } from "../../../../../logic/AssetManagement/Design/Theme/DesignThemeEndPointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../features/userSlice";
import { Link, useSearchParams } from "react-router-dom";

interface WidgetStructure {
  shared_uid: string;
  name: string;
  widget_meta: string;
}

function AdditionalContentTheme() {
  const { selectedThemeShareuid, designThemesData } = useSelector(
    (state: any) => state?.theme
  );
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [loading, setLoading] = useState(false);
  const selectedTheme = designThemesData?.find(
    (theme: any) => theme.shared_uid === selectedThemeShareuid
  );
  const templates = selectedTheme?.templates;
  const [widgetStructure, setWidgetStructure] = useState<WidgetStructure[]>([]); //  State for widget structure
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const fetchWebsiteStructure = async () => {
      setLoading(true);
      try {
        if (oauthTokenHeader) {
          await DesignThemeEndPointLogic.getWidgetStructures(
            oauthTokenHeader,
            setWidgetStructure
          );
        } else {
          throw new Error("Unauthorized access for template");
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchWebsiteStructure();
  }, [oauthTokenHeader]);

  const widgetNames = widgetStructure?.map((widget) => widget.name);
  const ComponentNames =
    templates?.map((template: any) => template.component) || [];
  const availableWidgetNames = widgetNames.filter(
    (name) => !ComponentNames.includes(name)
  );
  const labels =
    selectedTheme?.templates?.map((template: any) => template.labels) || [];
  const flattenedLabels = labels.flat(); // Flatten the array of arrays
  const selectedSite = searchParams.get("selectedSite");
  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <Typography>Assign Templates to Objects</Typography>
            <Box>
              <TableContainer
                component={Paper}
                className="themewrap"
                sx={{ maxHeight: 248, overflowY: "auto", boxShadow: "0" }}
              >
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ margin: "16px" }}>
                        #
                      </TableCell>
                      <TableCell align="left">Object Name</TableCell>
                      <TableCell align="left">Assigned Template</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates?.map((themes: any, index: number) => (
                      <TableRow
                        key={themes.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="left">{index + 1}</TableCell>
                        <TableCell align="left">{themes.component}</TableCell>
                        <TableCell align="left">
                          <div>
                            <Link
                              data-tracker="411"
                              to={`/assets/design/templates?selectedSite=${selectedSite}&search=${encodeURIComponent(
                                themes.name
                              ).replace(/%20/g, "+")}`}
                              target="_blank"
                              style={{
                                textDecoration: "none",
                                display: "inline-block",
                                cursor: "pointer",
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: "#ffffff",
                                  color: "#000",
                                  // height: "px",
                                  px: 1.5,
                                  py: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                  borderRadius: "4px",
                                  fontSize: "0.890rem",
                                  fontWeight: 500,
                                  cursor: "default",
                                  mr: 1,
                                  my: 0.5,
                                  transition: "color 0.2s ease-in-out",
                                  boxShadow:
                                    "2px 2px 6px rgba(0,0,0,0.15), -1px -1px 1px rgba(0,0,0,0.05)",
                                  "&:hover": {
                                    cursor: "pointer",
                                    color: "#9c27b0",
                                    boxShadow:
                                      "2px 2px 6px rgba(188, 109, 202, 0.6), -1px -1px 1px rgba(188, 109, 202, 0.3)",
                                  },
                                }}
                              >
                                {themes.name}
                              </Box>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography>Inactive Components</Typography>
            <Box>
              <TableContainer
                component={Paper}
                className="themewrap"
                sx={{ maxHeight: 248, overflowY: "auto", boxShadow: "0" }}
              >
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ margin: "16px" }}>
                        #
                      </TableCell>
                      <TableCell align="left">Object Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <SimpleBackdrop />
                    ) : (
                      availableWidgetNames?.map(
                        (widgetName: any, index: number) => (
                          <TableRow
                            key={widgetName}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell align="left">{index + 1}</TableCell>
                            <TableCell align="left">{widgetName}</TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography>All Related Groups</Typography>
            <Box mt={1}>
              <Grid container spacing={1}>
                {selectedTheme?.groups_appearance?.map(
                  (label: any, index: number) => (
                    <Grid item key={index}>
                      <Chip
                        label={label}
                        variant="outlined"
                        color="secondary"
                        sx={{ borderColor: "#9C27B0" }}
                      />
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography>Available Features in Theme</Typography>
            <Box mt={1}>
              <Grid container spacing={1}>
                {flattenedLabels?.map((label: any, index: number) => (
                  <Grid item key={index}>
                    <Chip
                      label={label}
                      variant="outlined"
                      color="secondary"
                      sx={{ borderColor: "#9C27B0" }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default AdditionalContentTheme;
